import { useCallback, useMemo } from 'react';
import { AppwideDrawerContentType, useAppwideDrawerContext } from '../../../../providers/AppwideDrawerProvider';
import { Button, ButtonVariants } from '../../../Button';
import { FormControlSizes } from '../../../Form';
import { IconName } from '../../../Icons/IconNames';
import type { EntityAdminPanelActionButton } from '../../types';
import { EntityAdminGuideToggle } from '../EntityAdminGuideToggle';
import type { EntityAdminWorkflowComponent } from './types';
import { WorkflowHeaderTitle } from './WorkflowHeader';

export function useEntityAdminWorkflow(
  WorkflowComponent: EntityAdminWorkflowComponent,
  { disabled = false }: { disabled?: boolean } = {}
): {
  startWorkflowAction: EntityAdminPanelActionButton;
  startWorkflowButton: JSX.Element;
} {
  const { openDrawer } = useAppwideDrawerContext();
  const { workflow } = WorkflowComponent;

  const startWorkflow = useCallback(() => {
    openDrawer({
      type: AppwideDrawerContentType.EntityAdmin,
      drawerHeaderDesign: 'flex',
      flexHeaderTitle: () => <WorkflowHeaderTitle title={workflow.title} />,
      renderHeaderContent: workflow.hasGuideText ? () => <EntityAdminGuideToggle /> : undefined,
      renderContent: () => <WorkflowComponent />,
    });
  }, [WorkflowComponent, openDrawer, workflow]);

  const startWorkflowAction = useMemo(
    () => ({
      label: workflow.title,
      onClick: startWorkflow,
      variant: ButtonVariants.Positive,
      startIcon: IconName.Plus,
      disabled,
    }),
    [disabled, startWorkflow, workflow.title]
  );

  const startWorkflowButton = useMemo(
    () => (
      <Button
        startIcon={startWorkflowAction.startIcon}
        onClick={startWorkflowAction.onClick}
        variant={startWorkflowAction.variant}
        size={FormControlSizes.Small}
        disabled={startWorkflowAction.disabled}
      >
        {startWorkflowAction.label}
      </Button>
    ),
    [startWorkflowAction]
  );

  return { startWorkflowAction, startWorkflowButton };
}
